import { motion } from "framer-motion";
import { Phone, ArrowDown, ShieldCheck, Package, Ruler } from "lucide-react";
import { TRUST_BADGES } from "../../data/content";
import { scrollToId } from "../../hooks/useSmoothScroll";

const HEADLINE_LINES = [
  "Tartós és megbízható",
  "tetőmegoldások több mint",
  "20 éves tapasztalattal",
];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 0.85, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const BADGE_ICONS = [ShieldCheck, Package, Ruler];

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-white pt-28 md:pt-36 pb-16 md:pb-24">
      <div className="pointer-events-none absolute -top-24 -right-24 h-[420px] w-[420px] rounded-full bg-emerald-400/10 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -left-24 h-[380px] w-[380px] rounded-full bg-[#0E5A75]/5 blur-3xl" />

      <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-12 gap-12 px-5 md:px-10 items-center">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 mb-7"
          >
            <span className="h-2 w-2 rounded-full bg-[#0E5A75] animate-pulse" />
            <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700">
              Tetőfedő szakértők · Győr és környéke
            </span>
          </motion.div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.02] text-[#0E5A75]">
            {HEADLINE_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span custom={i} variants={lineVariants} initial="hidden" animate="visible" className="block">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            Tetőfedés, tetőjavítás, tetőfelújítás, bádogos munkák és tetőszigetelés
            precíz kivitelezéssel, minőségi alapanyagokból.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.72 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => scrollToId("ajanlatkeres")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#0E5A75] px-7 py-4 text-base font-semibold text-white shadow-xl shadow-emerald-500/30 hover:bg-[#0E5A75] hover:scale-[1.03] transition-transform"
            >
              Helyszíni felmérést kérek
              <ArrowDown className="h-4 w-4 group-hover:translate-y-0.5 transition-transform" />
            </button>
            <a
              href="tel:+36209959700"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-4 text-base font-semibold text-[#0E5A75] hover:border-[#0E5A75] hover:bg-slate-50 transition-colors"
            >
              <Phone className="h-4 w-4" />
              Hívjon most: +36 20 995 9700
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-slate-200 pt-8"
          >
            {TRUST_BADGES.map((b, i) => {
              const Icon = BADGE_ICONS[i];
              return (
                <li key={i} data-testid={`hero-trust-${i}`} className="flex items-start gap-2.5">
                  <Icon className="h-5 w-5 shrink-0 text-emerald-500 mt-0.5" />
                  <span className="text-sm font-medium text-[#0E5A75] leading-snug">{b}</span>
                </li>
              );
            })}
          </motion.ul>
        </div>

        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div
              data-testid="hero-video"
              className="relative mx-auto w-full max-w-[260px] sm:max-w-[320px] lg:max-w-[380px] overflow-hidden rounded-[2rem] shadow-2xl shadow-[#0E5A75]/20"
              dangerouslySetInnerHTML={{
                __html: '<wistia-player media-id="cytvhi7ob0" aspect="0.5625" autoplay="true" muted="true" loop="true" end-video-behavior="loop" controls-visible-on-load="false" style="display:block;width:100%"></wistia-player>',
              }}
            />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1 }}
              className="absolute -bottom-6 left-1/2 -translate-x-1/2 sm:left-6 sm:translate-x-0 rounded-2xl bg-white p-4 sm:p-5 shadow-xl border border-slate-100 max-w-[220px] text-center sm:text-left"
            >
              <div className="text-3xl font-extrabold text-[#0E5A75]">20<span className="text-emerald-500">+</span></div>
              <div className="text-sm text-slate-500 leading-snug mt-1">év szakmai tapasztalat a tetőfedésben</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
